import {
  addDays,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  startOfDay,
  subYears,
} from "date-fns";
import type { DateValidationRules, CalendarDay } from "../types/dateTypes";

export const dateUtils = {
  /**
   * Format date to MM/DD/YYYY string
   */
  formatDate: (date: Date, format: string = "MM/DD/YYYY"): string => {
    if (!dateUtils.isValidDate(date)) return "";

    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear().toString();

    switch (format) {
      case "MM/DD/YYYY":
        return `${month}/${day}/${year}`;
      case "YYYY-MM-DD":
        return `${year}-${month}-${day}`;
      case "DD/MM/YYYY":
        return `${day}/${month}/${year}`;
      default:
        return `${month}/${day}/${year}`;
    }
  },

  /**
   * Parse date string to Date object
   */
  parseDate: (dateString: string): Date | null => {
    if (!dateString) return null;

    // Handle MM/DD/YYYY format
    const mmddyyyy = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mmddyyyy) {
      const [, month, day, year] = mmddyyyy;
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return dateUtils.isValidDate(date) ? date : null;
    }

    // Handle YYYY-MM-DD format
    const yyyymmdd = dateString.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (yyyymmdd) {
      const [, year, month, day] = yyyymmdd;
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return dateUtils.isValidDate(date) ? date : null;
    }

    // Fallback to Date constructor
    const date = new Date(dateString);
    return dateUtils.isValidDate(date) ? date : null;
  },

  /**
   * Check if date is valid
   */
  isValidDate: (date: Date): boolean => {
    return date instanceof Date && !isNaN(date.getTime());
  },

  /**
   * Check if two dates are the same day
   */
  isSameDay: (date1: Date, date2: Date): boolean => {
    if (!dateUtils.isValidDate(date1) || !dateUtils.isValidDate(date2))
      return false;

    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  },

  /**
   * Add days to a date
   */
  addDays: (date: Date, days: number): Date => {
    if (!dateUtils.isValidDate(date)) return new Date();

    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  },

  /**
   * Get today's date with time set to 00:00:00
   */
  getToday: (): Date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  },

  /**
   * Check if date is disabled based on validation rules
   */
  isDateDisabled: (date: Date, rules?: DateValidationRules): boolean => {
    if (!isValid(date) || !rules) return false;
    const { minDate, maxDate, disabledDates } = rules;

    const min = minDate ? startOfDay(minDate) : null;
    const max = maxDate ? startOfDay(maxDate) : null;

    // Disable ONLY if before minDate
    if (min && isBefore(date, min)) {
      return true;
    }

    // Disable ONLY if after maxDate
    if (max && isAfter(date, max)) {
      return true;
    }

    // Disabled specific dates (still allowed to override)
    if (disabledDates?.some((d) => isSameDay(date, d))) {
      return true;
    }
    return false;
  },

  /**
   * Get date range between two dates
   */
  getDateRange: (startDate: Date, endDate: Date): Date[] => {
    if (!dateUtils.isValidDate(startDate) || !dateUtils.isValidDate(endDate)) {
      return [];
    }

    const dates: Date[] = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  },

  /**
   * Check if device is mobile
   * Uses proper device detection instead of just screen width
   */
  isMobile: (): boolean => {
    // Check for touch capability and user agent
    const hasTouchScreen =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const isMobileUserAgent =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    // Only consider it mobile if it has touch AND is a mobile user agent
    // This prevents narrow desktop windows from being treated as mobile
    return hasTouchScreen && isMobileUserAgent;
  },

  /**
   * Get days in month
   */
  getDaysInMonth: (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  },

  /**
   * Get first day of month (0 = Sunday, 1 = Monday, etc.)
   */
  getFirstDayOfMonth: (year: number, month: number): number => {
    return new Date(year, month, 1).getDay();
  },

  /**
   * Get calendar days for a month
   */
  getCalendarDays: (
    year: number,
    month: number,
    selectedDate?: Date,
    validationRules?: DateValidationRules
  ): CalendarDay[] => {
    const today = dateUtils.getToday();
    const firstDay = dateUtils.getFirstDayOfMonth(year, month);
    const daysInMonth = dateUtils.getDaysInMonth(year, month);
    const daysInPrevMonth = dateUtils.getDaysInMonth(year, month - 1);

    const days: CalendarDay[] = [];

    // Previous month days
    for (let i = firstDay - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, daysInPrevMonth - i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: dateUtils.isSameDay(date, today),
        isSelected: selectedDate
          ? dateUtils.isSameDay(date, selectedDate)
          : false,
        isDisabled: dateUtils.isDateDisabled(date, validationRules),
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({
        date,
        isCurrentMonth: true,
        isToday: dateUtils.isSameDay(date, today),
        isSelected: selectedDate
          ? dateUtils.isSameDay(date, selectedDate)
          : false,
        isDisabled: dateUtils.isDateDisabled(date, validationRules),
      });
    }

    // Next month days to fill the grid (42 days total - 6 weeks)
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: dateUtils.isSameDay(date, today),
        isSelected: selectedDate
          ? dateUtils.isSameDay(date, selectedDate)
          : false,
        isDisabled: dateUtils.isDateDisabled(date, validationRules),
      });
    }

    return days;
  },

  /**
   * Get month names
   */
  getMonthNames: (): string[] => {
    return [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
  },

  /**
   * Get short month names
   */
  getShortMonthNames: (): string[] => {
    return [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
  },

  /**
   * Get day names
   */
  getDayNames: (): string[] => {
    return [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
  },

  /**
   * Get short day names
   */
  getShortDayNames: (): string[] => {
    return ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  },

  /**
   * Create validation rules for start date
   */
  createStartDateRules: (): DateValidationRules => {
    const today = dateUtils.getToday();
    const currentYear = today.getFullYear();

    // For Annual Multi-Trip, restrict to current year and onwards
    // Set minimum date to today, but ensure year picker only shows current year onwards
    return {
      minDate: today,
      maxDate: undefined,
      minYear: currentYear,
    };
  },

  /**
   * Create validation rules for end date based on start date
   */
  createEndDateRules: (
    startDate: Date,
    tripType: "Single Trip" | "Annual Multi-Trip" = "Single Trip"
  ): DateValidationRules => {
    if (!dateUtils.isValidDate(startDate)) {
      return {
        minDate: dateUtils.getToday(),
        maxDate: undefined,
      };
    }

    const maxDays = tripType === "Single Trip" ? 180 : 365;

    return {
      minDate: startDate,
      maxDate: dateUtils.addDays(startDate, maxDays - 1),
      tripType,
    };
  },

  /**
   * Create validation rules for Short Secure end date based on start date and plan type
   */
  createShortSecureEndDateRules: (
    startDate: Date,
    planType: "Individual" | "Group"
  ): DateValidationRules => {
    if (!dateUtils.isValidDate(startDate)) {
      return {
        minDate: dateUtils.getToday(),
        maxDate: undefined,
      };
    }

    // Short Secure Group: 5 days maximum, Individual: 15 days maximum
    const maxDays = planType === "Group" ? 4 : 14;

    return {
      minDate: startDate,
      maxDate: dateUtils.addDays(startDate, maxDays),
      tripType: "Single Trip",
      planType: "ShortSecure",
    };
  },

  /**
   * Create validation rules for birthdate (18-100 years old eligibility)
   */
  createBirthdateRules: (): DateValidationRules => {
    const today = dateUtils.getToday();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();

    // Calculate minimum birthdate (18 years ago from today)
    // Person must be at least 18 years old, so max birthdate is 18 years ago
    const minAge18Date = new Date(currentYear - 18, currentMonth, currentDay);

    // Calculate maximum birthdate (100 years ago from today)
    // Person can be at most 100 years old, so min birthdate is 100 years ago
    // TODO: uncomment when senior plans are back 
    // const maxAge100Date = new Date(currentYear - 100, 0, 1); // January 1st, 100 years ago


    const maxAge75Date = subYears(new Date(), 76); // December 31st, 1949
    const maxAgeDate = addDays(maxAge75Date, 1);

    // Set age range: 18-100 years old
    return {
      // TODO: uncomment when senior plans are back 
      // minDate: maxAge100Date, // 100 years ago (oldest allowed)
      minDate: maxAgeDate, // 100 years ago (oldest allowed)
      maxDate: minAge18Date, // 18 years ago (youngest allowed)
      // TODO: uncomment when senior plans are back 
      // minYear: currentYear - 100,
      minYear: currentYear - 76,
      maxYear: currentYear - 18,
    };
  },

    createSomeoneElseBirthdateRules: (): DateValidationRules => {
    const today = dateUtils.getToday();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();

    // Calculate minimum birthdate (0 years ago from today)
    // Person can be newborn, so max birthdate is today
    const minAge0Date = new Date(currentYear, currentMonth, currentDay);

    // Calculate maximum birthdate (75 years, 364 days ago from today)
    // Person can be at most 75 years old, so oldest allowed birthdate is
    // one day after 76 years ago (i.e. 75 years, 364 days old)
    const maxAge75Date = subYears(new Date(), 76); // December 31st, 1949
    const maxAgeDate = addDays(maxAge75Date, 1);

    // Set age range: 0-100 years old
    return {
      minDate: maxAgeDate, // 75 years, 364 days ago (oldest allowed)
      maxDate: minAge0Date, // Today (youngest allowed - newborn)
      minYear: currentYear - 76,
      maxYear: currentYear,
    };
  },

  /**
   * Create validation rules for Short Secure birthdate (0-75 years old eligibility)
   */
  createShortSecureBirthdateRules: (): DateValidationRules => {
    const today = dateUtils.getToday();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();

    // Calculate minimum birthdate (0 years ago from today)
    // Person can be newborn, so max birthdate is today
    const minAgeDate = new Date(currentYear, currentMonth, currentDay);


    // Calculate maximum birthdate (75 years ago from today)
    // Person can be at most 75 years old, so min birthdate is 75 years ago
    const maxAge75Date = subYears(new Date(), 76); // December 31st, 1949
    const maxAgeDate = addDays(maxAge75Date, 1);

    // Set age range: 0-75 years old
    return {
      minDate: maxAgeDate, // 75 years ago (oldest allowed)
      maxDate: minAgeDate, // Today (youngest allowed - newborn)
      minYear: currentYear - 76,
      maxYear: currentYear,
    };
  },

  /**
   * Create validation rules for child birthdate (0-17 years old eligibility)
   */
  createChildBirthdateRules: (): DateValidationRules => {
    const today = dateUtils.getToday();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();

    // Calculate minimum birthdate (17 years ago from today)
    // Child can be at most 17 years old, so min birthdate is 17 years ago
    const maxAge17Date = new Date(currentYear - 17, currentMonth, currentDay);

    // Calculate maximum birthdate (today)
    // Child can be newborn (0 years old), so max birthdate is today
    const minAge0Date = today;

    // Set age range: 0-17 years old
    return {
      minDate: maxAge17Date, // 17 years ago (oldest allowed child)
      maxDate: minAge0Date, // Today (youngest allowed - newborn)
      minYear: currentYear - 17,
      maxYear: currentYear,
    };
  },

  /**
   * Create validation rules for family plan birthdate (18-75 years old eligibility)
   * This is specifically for family plans where both principal insured and spouse
   * must be between 18-75 years old as stated in the Family Plan Modal
   */
  createFamilyPlanBirthdateRules: (): DateValidationRules => {
    console.log("🔥 FAMILY PLAN BIRTHDATE RULES CALLED - UPDATED VERSION");

    const today = dateUtils.getToday();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();

    // Calculate minimum birthdate (18 years ago from today)
    // Person must be at least 18 years old, so max birthdate is 18 years ago
    const minAge18Date = new Date(currentYear - 18, currentMonth, currentDay);

    // Calculate maximum birthdate (75 years ago from today)
    // To allow the entire year 1950 (people who are 75 years old), we need to set
    // the minimum date to December 31st, 1949. This ensures all dates in 1950 are valid.
    const maxAge75Date = subYears(new Date(), 76); // December 31st, 1949
    const maxAgeDate = addDays(maxAge75Date, 1);
    const rules = {
      minDate: maxAgeDate, // December 31st, 1949 (allows all of 1950)
      maxDate: minAge18Date, // 18 years ago (youngest allowed)
      minYear: currentYear - 76, // Allow birth year 1950 (75 years old)
      maxYear: currentYear - 18,
    };

    console.log("🔥 Family Plan Rules:", {
      minDate: dateUtils.formatDate(rules.minDate),
      maxDate: dateUtils.formatDate(rules.maxDate),
      minYear: rules.minYear,
      maxYear: rules.maxYear,
      test1950Jan1: new Date(1950, 0, 1) >= rules.minDate,
      test1950Dec31: new Date(1950, 11, 31) >= rules.minDate,
    });

    // Set age range: 18-75 years old for family plans
    return rules;
  },

  /**
   * Calculate age from birth date
   */
  calculateAge: (birthDate: Date): number => {
    if (!dateUtils.isValidDate(birthDate)) return 0;

    const today = dateUtils.getToday();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // Adjust age if birthday hasn't occurred this year yet
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  },

  /**
   * Format travel date range in the format "Jul 20, 2025 - Jul 28, 2025"
   * Both start and end dates use short month names
   */
  formatTravelDateRange: (startDateStr: string, endDateStr: string): string => {
    if (!startDateStr || !endDateStr) return "";

    const startDate = dateUtils.parseDate(startDateStr);
    const endDate = dateUtils.parseDate(endDateStr);

    if (!startDate || !endDate) return "";

    const shortMonthNames = dateUtils.getShortMonthNames();

    // Format start date: "Jul 20, 2025"
    const startMonth = shortMonthNames[startDate.getMonth()];
    const startDay = startDate.getDate();
    const startYear = startDate.getFullYear();
    const formattedStartDate = `${startMonth} ${startDay}, ${startYear}`;

    // Format end date: "Jul 28, 2025"
    const endMonth = shortMonthNames[endDate.getMonth()];
    const endDay = endDate.getDate();
    const endYear = endDate.getFullYear();
    const formattedEndDate = `${endMonth} ${endDay}, ${endYear}`;

    return `${formattedStartDate} - ${formattedEndDate}`;
  },

  /**
   * Format coverage period date range in the format "July 20, 2025 - July 28, 2025"
   * Both dates use full month names
   */
  formatCoveragePeriodRange: (
    startDateStr: string,
    endDateStr: string
  ): string => {
    if (!startDateStr || !endDateStr) return "";

    const startDate = dateUtils.parseDate(startDateStr);
    const endDate = dateUtils.parseDate(endDateStr);

    if (!startDate || !endDate) return "";

    const fullMonthNames = dateUtils.getMonthNames();

    // Format start date: "July 20, 2025"
    const startMonth = fullMonthNames[startDate.getMonth()];
    const startDay = startDate.getDate();
    const startYear = startDate.getFullYear();
    const formattedStartDate = `${startMonth} ${startDay}, ${startYear}`;

    // Format end date: "July 28, 2025"
    const endMonth = fullMonthNames[endDate.getMonth()];
    const endDay = endDate.getDate();
    const endYear = endDate.getFullYear();
    const formattedEndDate = `${endMonth} ${endDay}, ${endYear}`;

    return `${formattedStartDate} - ${formattedEndDate}`;
  },
};
